import React, { useEffect, useState } from "react";
import { api, fmtCurrency, fmtDay, downloadOrderDocument } from "@/lib/api";
import { Link } from "react-router-dom";
import { StatusBadge, PaymentBadge } from "@/components/StatusBadge";
import { Sparkles, FileText, Receipt, Download, Building2, BadgeCheck } from "lucide-react";
import { downloadQuotation, downloadInvoice, downloadEnquiry } from "@/lib/pdf";
import { toast } from "sonner";
import SocialLinks from "@/components/SocialLinks";

const ENQ_COLORS = { new: "#D4AF37", reviewed: "#3B82F6", quoted: "#A78BFA", confirmed: "#10B981", declined: "#EF4444" };

export default function CustomerPortalPage() {
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/enquiries/my").then((r) => setEnquiries(r.data)).catch(() => {});
  }, []);

  const downloadDoc = async (orderId, doc) => {
    try { await downloadOrderDocument(orderId, doc); }
    catch { toast.error("Download failed, please try again"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="customer-portal-page">
      <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">My Account</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Your Events</h1>
          <p className="text-zinc-500 mt-2 text-sm">Every detail, every moment, beautifully tracked.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/plan-event" data-testid="plan-event-cta"
            className="flex items-center gap-2 px-5 py-3 rounded-sm bg-[#D4AF37] text-black hover:bg-[#E5C86B] transition-colors text-sm font-medium">
            <Sparkles className="w-4 h-4" /> HNC Customer Order
          </Link>
          <Link to="/corporate" data-testid="corporate-cta"
            className="flex items-center gap-2 px-5 py-3 rounded-sm border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors text-sm font-medium">
            <Building2 className="w-4 h-4" /> Corporate Quotation
          </Link>
        </div>
      </div>

      {enquiries.length > 0 && (
        <div className="mb-10">
          <div className="label-eyebrow mb-3">My Enquiries</div>
          <div className="space-y-2">
            {enquiries.map((e) => (
              <div key={e.id} className="card-lux p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`my-enquiry-${e.id}`}>
                <div>
                  <div className="text-sm">{e.event_name || (e.type === "corporate" ? e.company_name : "Event enquiry")} · <span className="font-mono text-[10px] text-[#D4AF37]">{e.ref_code}</span></div>
                  <div className="text-xs text-zinc-500 mt-0.5">{fmtDay(e.event_date)} · {e.pax} pax</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="stage-chip capitalize" style={{ color: ENQ_COLORS[e.status], borderColor: ENQ_COLORS[e.status] + "55", background: ENQ_COLORS[e.status] + "10" }}>{e.status}</span>
                  <button onClick={() => downloadEnquiry(e)} data-testid={`enq-pdf-${e.id}`} title="Download enquiry PDF"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-[11px]">
                    <Download className="w-3 h-3" /> PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {orders.length === 0 && (
          <div className="card-lux p-10 text-center">
            <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
            <h3 className="font-display text-2xl">No events yet</h3>
            <p className="text-zinc-500 mt-2 text-sm">Your upcoming events will appear here. Contact us to plan your next soirée.</p>
          </div>
        )}
        {orders.map((o) => (
          <div key={o.id} className="card-lux p-6" data-testid={`portal-order-${o.id}`}>
            <Link to={`/orders/${o.id}`} className="block hover:opacity-90 transition-opacity">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="label-eyebrow">{o.event_type}</div>
                  <div className="font-display text-2xl mt-1">{o.package_name}</div>
                  <div className="text-sm text-zinc-400 mt-1">{o.event_location}</div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <StatusBadge status={o.status} />
                  <PaymentBadge order={o} />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-[#1a1a1a]">
                <Cell label="Date" value={fmtDay(o.event_date)} />
                <Cell label="Pax" value={o.pax} />
                <Cell label="Total" value={fmtCurrency(o.total_amount)} />
                <Cell label="Balance" value={fmtCurrency((o.total_amount || 0) - (o.deposit_paid || 0))} tint="#F59E0B" />
              </div>
            </Link>
            <div className="flex gap-2 mt-4 pt-4 border-t border-[#1a1a1a]">
              <button onClick={() => downloadQuotation(o)} data-testid={`portal-quote-${o.id}`}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-xs">
                <FileText className="w-3.5 h-3.5" /> Quotation PDF
              </button>
              <button onClick={() => downloadInvoice(o)} data-testid={`portal-invoice-${o.id}`}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-xs">
                <Receipt className="w-3.5 h-3.5" /> Invoice PDF
              </button>
            </div>
            {(o.documents || []).length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#1a1a1a]" data-testid={`portal-docs-${o.id}`}>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">
                  <BadgeCheck className="w-3.5 h-3.5" /> Official Documents
                </div>
                <div className="space-y-1.5">
                  {o.documents.map((d) => (
                    <button key={d.id} onClick={() => downloadDoc(o.id, d)} data-testid={`portal-doc-${d.id}`}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/5 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-xs">
                      <span className="flex items-center gap-2 min-w-0">
                        {d.doc_type === "receipt" ? <Receipt className="w-3.5 h-3.5 shrink-0" /> : <FileText className="w-3.5 h-3.5 shrink-0" />}
                        <span className="truncate">{d.original_filename}</span>
                        <span className="text-[10px] text-zinc-500 capitalize shrink-0">· {d.doc_type}</span>
                      </span>
                      <Download className="w-3.5 h-3.5 shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <SocialLinks />
    </div>
  );
}

function Cell({ label, value, tint }) {
  return (
    <div>
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="text-sm mt-0.5" style={{ color: tint || "#FAFAFA" }}>{value}</div>
    </div>
  );
}
